import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

// Interface para quando usar objeto com key/label
export interface NfTableColumn {
  key: string;
  label: string;
}

// Tipo para aceitar tanto string[] quanto NfTableColumn[]
export type TableColumns = string[] | NfTableColumn[];

export interface NfTableRow {
  [key: string]: any;
}

export interface ServerSidePagination {
  totalRecords: number;
  page: number;
  pageSize: number;
  from: number;
  to: number;
}

@Component({
  selector: 'app-nf-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './nf-table.html',
  styleUrls: ['./nf-table.scss']
})
export class NfTableComponent implements OnInit, OnChanges {
  // Aceita tanto string[] quanto NfTableColumn[]
  @Input() columns: TableColumns = [];
  @Input() data: NfTableRow[] = [];
  @Input() serverSidePagination?: ServerSidePagination;
  @Input() showPagination: boolean = false;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  currentPage: number = 0;
  pageSize: number = 5;

  // Para uso interno - sempre converte para array de objetos
  processedColumns: NfTableColumn[] = [];

  ngOnInit() {
    this.processColumns();
    this.applyPagination();
  }

  ngOnChanges() {
    this.processColumns();
    this.applyPagination();
  }

  // Converte columns para formato interno padronizado
  private processColumns(): void {
    if (Array.isArray(this.columns)) {
      if (this.columns.length > 0 && typeof this.columns[0] === 'string') {
        // Se for string[], converte para NfTableColumn[]
        this.processedColumns = (this.columns as string[]).map(col => ({
          key: col,
          label: col
        }));
      } else {
        // Já é NfTableColumn[]
        this.processedColumns = this.columns as NfTableColumn[];
      }
    } else {
      this.processedColumns = [];
    }
  }

  // Obtém os nomes das colunas para exibição
  get columnKeys(): string[] {
    return this.processedColumns.map(col => col.key);
  }

  get columnLabels(): string[] {
    return this.processedColumns.map(col => col.label);
  }

  get filteredPaginatedData(): NfTableRow[] {
    // Se não mostrar paginação, retorna todos os dados
    if (!this.showPagination) {
      return this.data;
    }

    if (this.serverSidePagination) {
      return this.data;
    }
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.data.slice(startIndex, endIndex);
  }

  get totalItems(): number {
    if (this.serverSidePagination) {
      return this.serverSidePagination.totalRecords;
    }
    return this.data.length;
  }

  private applyPagination(): void {
    this.currentPage = 0;
  }

  getDisplayedRange(): string {
    if (!this.showPagination) {
      return `${this.data.length} itens`;
    }

    if (this.serverSidePagination) {
      const { from, to, totalRecords } = this.serverSidePagination;
      return `${from}-${to} de ${totalRecords}`;
    }
    if (this.totalItems === 0) return '0-0 de 0';
    const start = this.currentPage * this.pageSize + 1;
    const end = Math.min((this.currentPage + 1) * this.pageSize, this.totalItems);
    return `${start}-${end} de ${this.totalItems}`;
  }

  onPageSizeChange(event: any): void {
    if (!this.showPagination) return;

    const newPageSize = +event.target.value;
    this.pageSize = newPageSize;
    this.currentPage = 0;

    if (this.serverSidePagination) {
      this.pageSizeChange.emit(newPageSize);
    }
  }

  previousPage(): void {
    if (!this.showPagination) return;

    if (this.serverSidePagination) {
      const newPage = this.serverSidePagination.page - 1;
      if (newPage >= 1) {
        this.pageChange.emit(newPage);
      }
    } else {
      if (this.currentPage > 0) this.currentPage--;
    }
  }

  nextPage(): void {
    if (!this.showPagination) return;

    if (this.serverSidePagination) {
      const newPage = this.serverSidePagination.page + 1;
      if (this.serverSidePagination.to < this.serverSidePagination.totalRecords) {
        this.pageChange.emit(newPage);
      }
    } else {
      if ((this.currentPage + 1) * this.pageSize < this.totalItems) this.currentPage++;
    }
  }

  hasPreviousPage(): boolean {
    if (!this.showPagination) return false;

    if (this.serverSidePagination) {
      return this.serverSidePagination.page > 1;
    }
    return this.currentPage > 0;
  }

  hasNextPage(): boolean {
    if (!this.showPagination) return false;

    if (this.serverSidePagination) {
      return this.serverSidePagination.to < this.serverSidePagination.totalRecords;
    }
    return (this.currentPage + 1) * this.pageSize < this.totalItems;
  }
}
